import { AxiosInstance, AxiosRequestConfig } from 'axios'

import {
  BadRequestError,
  HttpStatusCode,
  NotFoundError,
  Success,
  TooManyRequestError,
  UnauthorizedUserError,
  UnprocessableEntityError,
} from '@/api/results'
import {
  ChangeUserInfoDTO,
  ConfirmCode,
  RequestCode,
  SignIn,
  UserInfo,
  UserStatistics,
} from '~/api/modules/user/types'

/* Utils */
import {
  getMappedRequestedCode,
  getMappedUserInfo,
  getMappedUserStatistics,
} from '@/api/modules/user/user.utils'
import { BonusHistory } from '~/types/api/api-v3/bonus-history'
import { OrdersPagination } from '~/types/api/api-v3/orders'

export function createUserModule({
  axios,
  sentry,
  city,
  hostname,
}: {
  axios: AxiosInstance
  sentry: any
  city: string
  hostname: string
}) {
  return {
    async getInfo(header: AxiosRequestConfig) {
      try {
        const { data } = await axios.get<UserInfo>(`/${city}/auth/me/`, header)

        return new Success(getMappedUserInfo(data))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({ message: 'Вы не авторизованны' })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async getStatistics() {
      try {
        const { data } = await axios.get<UserStatistics>(`/${city}/client/`)

        return new Success(getMappedUserStatistics(data))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({ message: 'Вы не авторизованны' })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async changeInfo(requestData: ChangeUserInfoDTO) {
      try {
        await axios.post(`/${city}/client/change/`, requestData)

        return new Success()
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({ message: 'Вы не авторизованны' })
          case HttpStatusCode.BAD_REQUEST:
            return new BadRequestError({
              message: 'Некорректный запрос',
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async changePassword(requestData: { password: string }) {
      try {
        await axios.post(`/${city}/auth/password/`, requestData)

        return new Success()
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({ message: 'Вы не авторизованны' })
          case HttpStatusCode.BAD_REQUEST:
            return new BadRequestError({
              message: 'Некорректный запрос',
              data: error.response.data,
            })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async callback(requestData: { phone: string }) {
      try {
        await axios.post(`/${city}/callback/`, requestData)

        return new Success()
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.TOO_MANY_REQUESTS: {
            const timeout: number = error.response?.headers['retry-after'] || 0
            return new TooManyRequestError(
              'Запрос выполняется слишком часто',
              timeout,
            )
          }
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async signOut() {
      try {
        await axios.post(`/${city}/auth/sign_out/`)

        return new Success()
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({ message: 'Вы не авторизованны' })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async getOrders(pageNumber: number) {
      try {
        const { data } = await axios.get<OrdersPagination>(
          `/${city}/orders/?page=${pageNumber}`,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({ message: 'Вы не авторизованны' })
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async getBonusHistory(page: number) {
      try {
        const { data } = await axios.get<BonusHistory>(
          `/${city}/client/bonus_history/`,
          {
            params: {
              page,
            },
          },
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Ошибка бизнес логики',
              data: error.response.data,
            })
          case HttpStatusCode.NOT_FOUND:
            return new NotFoundError('Что-то пошло не так')
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async signIn(requestData: { phone: string; password: string }) {
      try {
        const { data } = await axios.post<SignIn>(
          `/${city}/auth/sign_in/`,
          requestData,
        )

        return new Success(getMappedUserInfo(data))
      } catch (error) {
        switch (error?.response?.status) {
          case HttpStatusCode.UNAUTHORIZED:
            return new UnauthorizedUserError({
              message: 'Вы не авторизованны',
              data: error.response.data,
            })
          case HttpStatusCode.BAD_REQUEST:
            return new BadRequestError({
              message: 'Некорректный запрос',
              data: error.response.data,
            })
          case HttpStatusCode.TOO_MANY_REQUESTS: {
            const timeout: number = error.response?.headers['retry-after'] || 0
            return new TooManyRequestError(
              'Запрос выполняется слишком часто',
              timeout,
            )
          }
          case HttpStatusCode.NOT_FOUND: {
            return new NotFoundError('Пользователь не найден')
          }
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async requestCode(requestData: { phone: string }) {
      try {
        const { data } = await axios.post<RequestCode>(
          `/${city}/auth/request_code/`,
          requestData,
        )

        return new Success(getMappedRequestedCode(data))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.BAD_REQUEST:
            return new BadRequestError({
              message: 'Некорректный запрос',
              data: error.response.data,
            })
          case HttpStatusCode.TOO_MANY_REQUESTS: {
            const timeout: number = error.response?.headers['retry-after'] || 0
            return new TooManyRequestError(
              'Запрос выполняется слишком часто',
              timeout,
            )
          }
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async confirmCode(requestData: { phone: string; code: string }) {
      try {
        const { data } = await axios.post<ConfirmCode>(
          `/${city}/auth/confirm/`,
          requestData,
        )

        return new Success(getMappedUserInfo(data))
      } catch (error) {
        switch (error.response?.status) {
          case HttpStatusCode.UNPROCESSABLE_ENTITY:
            return new UnprocessableEntityError({
              message: 'Ошибка бизнес логики',
              data: error.response.data,
            })
          case HttpStatusCode.BAD_REQUEST:
            return new BadRequestError({
              message: 'Некорректный запрос',
              data: error.response.data,
            })
          case HttpStatusCode.TOO_MANY_REQUESTS: {
            const timeout: number = error.response?.headers['retry-after'] || 0
            return new TooManyRequestError(
              'Запрос выполняется слишком часто',
              timeout,
            )
          }
          default:
            sentry.captureException(error)
            return new Error('Что-то пошло не так')
        }
      }
    },
    async vkAuth(uuid: string, token: string, first_name: string) {
      try {
        const { data } = await axios.post(`/${city}/auth/vkid/`, {
          silent_token: token,
          uuid,
          first_name,
          platform: 'web',
        })

        return new Success(data)
      } catch (error) {
        sentry.captureException(error)

        switch (error.response?.status) {
          default:
            return new Error('Что-то пошло не так')
        }
      }
    },
    async deleteSavedCard(numberCard: string) {
      try {
        const domen2level = hostname.split('.').slice(-2).join('.')

        const { data } = await axios.delete(
          `https://api.${domen2level}/v3/online_payments/pay_selection_cards/${numberCard}`,
        )

        return new Success(data)
      } catch (error) {
        switch (error.response?.status) {
          default:
            sentry.captureException(error)
            return new Error(error.response?.status)
        }
      }
    },
  }
}
